import { CCSRouteMap, type IEstate, type ISalesPackage, type ISalesPackageOptions, type SalesPackageEvents } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, unref } from 'vue';

import {
    type ICreateSalesPackageParams,
    type IUpdateSalesPackageParams,
    addEstatesToSalesPackage,
    createSalesPackage,
    fetchSalesPackage,
    fetchSalesPackageEligibleEstates,
    fetchSalesPackages,
    getSalesPackageDocuments,
    getSalesPackageTitles,
    triggerSalesPackageEvent,
    updateSalesPackage,
    updateSalesPackageEstates,
} from '@/api/sales-package.api';
import { pushToQuery, trimNilOrEmptyParameters, withLoading } from '@/shared';
import type { MaybeRef } from '@/shared/typings';

interface ISalesPackageParams {
    view?: string;
    withDocumentInspection?: boolean;
}

export const useSalesPackage = (salesPackageIdOrRef: MaybeRef<number>, params?: ISalesPackageParams) => {
    const salesPackageId = computed(() => unref(salesPackageIdOrRef));
    const queryEnabled = computed(() => !!salesPackageId.value);

    const queryClient = useQueryClient();

    const query = useQuery(
        ['sales-packages', salesPackageId, params?.view],
        async (): Promise<ISalesPackage> => fetchSalesPackage(salesPackageId.value, params),
        {
            initialData: null,
            enabled: queryEnabled,
        },
    );

    const { mutateAsync: updateSalesPackageMutation } = useMutation(
        async (salesPackageDto: IUpdateSalesPackageParams) => updateSalesPackage(salesPackageId.value, salesPackageDto),
        {
            onSuccess: data => {
                queryClient.setQueryData(['sales-packages', salesPackageId.value, params?.view], data);
            },
        },
    );

    const { mutateAsync: triggerSalesPackageEventMutation } = useMutation(
        async (event: SalesPackageEvents) => triggerSalesPackageEvent(salesPackageId.value, event),
        {
            onSuccess: data => {
                queryClient.setQueryData(['sales-packages', salesPackageId.value, params?.view], data);
            },
        },
    );

    const { mutateAsync: updateEstatesMutation } = useMutation(
        async (estates: { estateId: number; salesPackageUnitNumber: number }[]) =>
            updateSalesPackageEstates(salesPackageId.value, { estates, withEstateRelations: true }),
        {
            onSuccess: data => {
                queryClient.setQueryData(['sales-packages', salesPackageId.value, params?.view], data);
            },
        },
    );

    return {
        ...query,
        update: updateSalesPackageMutation,
        triggerEvent: triggerSalesPackageEventMutation,
        updateEstates: updateEstatesMutation,
    };
};

export const useSalesPackages = (queryOpts?: ISalesPackageOptions, enabled = true) => {
    const queryClient = useQueryClient();

    const internalFilter = ref<ISalesPackageOptions>({});

    const query = useQuery(
        ['sales-packages', queryOpts],
        async (): Promise<{ salesPackages: ISalesPackage[]; total: number }> =>
            withLoading(async () =>
                fetchSalesPackages(
                    trimNilOrEmptyParameters({
                        ...queryOpts,
                        ...internalFilter.value,
                    }),
                ),
            ),
        {
            initialData: { salesPackages: [], total: 0 },
            enabled,
        },
    );

    const { mutateAsync: addEstatesMutation } = useMutation(
        async ({ salesPackageId, estates }: { salesPackageId: number; estates: { estateId: number; salesPackageUnitNumber: number }[] }) =>
            addEstatesToSalesPackage(salesPackageId, estates),
        {
            onSuccess: updatedSalesPackage => {
                queryClient.invalidateQueries(['sales-packages', updatedSalesPackage.salesPackageId]);
            },
        },
    );

    const { mutateAsync: createMutation } = useMutation(async (salesPackageDto: ICreateSalesPackageParams) => createSalesPackage(salesPackageDto));

    return { ...query, internalFilter, addEstatesToSalesPackage: addEstatesMutation, createSalesPackage: createMutation };
};

export const useSalesPackageEligibleEstates = filter => {
    const queryFilter = computed(() => filter.value);

    return useQuery(
        ['sales-packages/eligible-estates', queryFilter],
        async (): Promise<{ estates: IEstate[]; total: number }> => {
            pushToQuery(CCSRouteMap['selling.eligible-estates'].name, queryFilter.value);
            return withLoading(async () => fetchSalesPackageEligibleEstates(trimNilOrEmptyParameters(queryFilter.value)));
        },
        {
            initialData: { estates: [], total: 0 },
        },
    );
};

export const useSalesPackageTitles = () => {
    const query = useQuery(
        ['sales-package-titles'],
        async (): Promise<{ title?: string; salesPackageId?: number }[]> => {
            return getSalesPackageTitles();
        },
        { initialData: [] },
    );

    return query;
};

export const useSalesPackageDocuments = (salesPackageId: number) => {
    const queryClient = useQueryClient();
    const query = useQuery(['sales-package-documents', salesPackageId], () => getSalesPackageDocuments(salesPackageId), {
        initialData: { salesPackageDocuments: [], sellingRegions: [] },
    });

    return { ...query, refetchDocuments: () => queryClient.invalidateQueries(['sales-package-documents', salesPackageId]) };
};
